/**
 * Employee filter of the planner (#2038): narrows the shift blocks in the By Group views
 * and the rows in the By Employee views to one employee.
 */
import type { Shift } from '@/types/shifts';

export interface FilterableEmployee {
  id?: string;
  status?: string;
  allGroups?: boolean;
  groupIds?: string[];
}

/** The shifts and employee rows the grid shows for an employee filter (null = everyone). */
export function applyEmployeeFilter<E>(
  shifts: Shift[],
  employeeMap: Map<string, E>,
  employeeId: string | null
): { shifts: Shift[]; employeeMap: Map<string, E> } {
  if (!employeeId) return { shifts, employeeMap };
  const employee = employeeMap.get(employeeId);
  return {
    shifts: shifts.filter((shift) => shift.employee_id === employeeId),
    employeeMap: employee ? new Map([[employeeId, employee]]) : new Map(),
  };
}

/**
 * The employees the filter offers: active (or upcoming) ones, and with a childcare
 * selected, those in one of its groups or in all groups.
 */
export function employeesForFilter<E extends FilterableEmployee>(
  employees: E[],
  childcareGroupIds: string[] | null
): E[] {
  return employees.filter((employee) => {
    if (employee.status && employee.status !== 'ACTIVE' && employee.status !== 'UPCOMING') return false;
    if (!childcareGroupIds) return true;
    return employee.allGroups === true || (employee.groupIds ?? []).some((g) => childcareGroupIds.includes(g));
  });
}
